/**
 * useResponsiveViewer — Determines whether the viewer should show fullscreen
 * or in a 3:4 device frame based on viewport size and orientation.
 *
 * Extrahiert aus app/src/hooks/useResponsiveViewer.ts (Plan A Mapping-Audit
 * 2026-05-22 + Bene-Decision Q1: PhoneFrame-Size wie portal.scrolly.com).
 *
 * Rules:
 * - Desktop (>1024px wide): 3:4 device frame with dark background
 * - Tablet landscape (769–1024px, landscape): 3:4 device frame
 * - Tablet portrait (769–1024px, portrait): fullscreen
 * - Mobile (≤768px, any orientation): fullscreen
 *
 * Ersetzt public/-useIsMobileViewport (Block 2.6 P2 — duplicate Logic).
 *
 * Plan-N2 Block 3 v3 (2026-05-25 Bene): Lazy-Init wiederhergestellt nach
 * dem app/-Pattern. Bene-Quote: "guck bitte genau, wie wir die
 * verschiedenen anzeigen OS/IOS auf portal.scrolly.com nutzen. Da
 * hat alles gut geklappt." — Der app/-Hook (app/src/hooks/
 * useResponsiveViewer.ts) macht den richtigen Lazy-Init mit
 * `window.matchMedia` im useState-Initializer. Funktioniert dort
 * problemlos weil app/ und portal/ reine SPAs sind (kein SSR).
 *
 * Diese package-Variante wurde bei der Extraction "SSR-safe" gemacht
 * (default false, useEffect updated) um React #418 zu vermeiden — aber
 * das verursacht im public/-Vike-SSR-Stack einen weißen Phone-Mockup-
 * Flash auf Mobile-First-Render. Bene-Befund 2026-05-25: "ich sehe
 * dieses weisses feld immer kurz bevor dann der richtige screen kommt."
 *
 * Lösung (multi-Layer):
 *  1. Lazy-Init mit matchMedia restored (wie app/) — funktioniert auf
 *     SPA-Konsumenten ohne Anpassung
 *  2. Optionaler `initialIsCompact` Param für SSR-Konsumenten
 *     (public/-Stack): pageContext.viewportHint aus UA-Sniffing
 *     (renderer/+onRenderHtml.tsx) → Server-Render und Client-First-
 *     Render haben identischen Wert → kein Hydration-Mismatch
 *  3. useEffect-matchMedia bleibt als post-mount-Correction für
 *     Tablet-Edge-Cases (Desktop-UA aber Portrait-Mode etc.)
 */
import { useEffect, useState } from "react";

/**
 * Wann der Viewer VOLLBILD zeigt statt Phone-Frame (Jan + Benedikt,
 * 11.09.2026): Handys (bis 768 px), Tablets hochkant (bis 1024 px) und —
 * neu — Touch-Tablets in JEDER Ausrichtung (iPad quer bis iPad Pro 12,9",
 * 1366 px): ein grober Zeiger ohne Hover ist ein Touch-Geraet, ein Desktop
 * mit Touchscreen meldet weiter "fine"/"hover". Exportiert, damit die
 * Viewer-Huelle (public/ViewerChrome) DIESELBE Query in ihr CSS schreibt.
 */
export const FULLSCREEN_QUERY =
  "(max-width: 768px), (max-width: 1024px) and (orientation: portrait), (max-width: 1366px) and (hover: none) and (pointer: coarse)";

export function useResponsiveViewer(
  initialIsCompact = false,
  hydrationSafe = false,
): {
  isCompact: boolean;
} {
  // Lazy-Init Pattern aus app/-Hook:
  //   - Server-Render (typeof window === undefined): nutze
  //     initialIsCompact-Hint vom Caller (default false, oder
  //     pageContext.viewportHint im public/-Stack)
  //   - Client-First-Render: lies sofort matchMedia → echter Wert ohne
  //     warten auf useEffect. Auf SPA-Konsumenten (app/, portal/) hat
  //     der Hook keinen SSR-Conflict.
  // hydrationSafe (2026-08-10, Fix React-#418): SSR-Konsumenten (public/-Vike)
  // MÜSSEN im ersten Client-Render denselben Wert wie der SSR liefern — sonst
  // divergiert die viewport-abhängige Struktur (u.a. SideActions-Rail) → React
  // wirft "Hydration failed" hart. Mit true startet der State mit
  // initialIsCompact (= SSR-viewportHint); der useEffect korrigiert direkt nach
  // Mount auf den echten matchMedia-Wert. SPA-Aufrufer (app/) lassen false →
  // unverändertes Verhalten, kein Flash.
  const [isCompact, setIsCompact] = useState<boolean>(() => {
    if (hydrationSafe) return initialIsCompact;
    if (typeof window === "undefined") return initialIsCompact;
    if (typeof window.matchMedia !== "function") return initialIsCompact;
    return window.matchMedia(FULLSCREEN_QUERY).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia(FULLSCREEN_QUERY);
    const handler = (e: MediaQueryListEvent) => setIsCompact(e.matches);
    setIsCompact(mq.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  return { isCompact };
}
